import { FormattedDate } from 'components/misc/FormattedDate';
import { MissingValue } from 'components/misc/MissingValue';
import { Button } from 'components/ui/button';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useReadAiChatFigureRowsQuery } from 'store/api/aiChatFigureApi';
import type { FigureCell, FigureDefinition, FigureRows } from 'types/aiChatFigure';
import { cn } from 'utils/cn';
import { pathForResult } from 'utils/globalSearchRoutes';
import { dateStringToDateObject } from 'utils/parseDate';
import { useAiChatInternalLinkHandler } from '../../../aiChatInternalLinkContext';
import { VIEW_BRIEF_CLASS_NAME } from '../../MeetingPrepViewButton';
import { isModifiedClick } from '../isModifiedClick';
import {
    BriefSection,
    HEADER_WRAP_CLASS_NAME,
    KEY_NUMBERS_CLASS_NAME,
    KeyNumbers,
    KeyNumbersSkeleton,
    SECTION_BODY_CLASS_NAME,
    SectionReveal,
} from './BriefFigure';
import { useFigureExport } from './FigureDownload';
import { FigureFrame } from './FigureFrame';
import { figureCount, figureSort, figureTile } from './figureMeta';
import { recordOf } from './figureRows';
import { FigureTable } from './FigureTable';
import { RecordFields, RecordFieldsSkeleton } from './RecordFigure';
import { FigureReveal, FigureStatus } from './RowsFigure';

// The contact fields the header reads: its record, the meta line and the key numbers.
const HEADER_FIELDS = [
    'contact',
    'relationshipTypeName',
    'statusName',
    'segmentTypeName',
    'serviceLevelTypeName',
    'caseCount',
    'lastBdEffort',
];

// The contact's details under the key numbers, each left out when empty: how to reach it and who owns it. They are a
// record read of their own: a table read holds the header's seven fields and at most one more.
const DETAIL_FIELDS: [field: string, labelId: string][] = [
    ['mainEmail', 'email'],
    ['mainPhone', 'phone'],
    ['mainMobile', 'mobile'],
    ['partners', 'partners'],
    ['managers', 'managers'],
    ['businessContacts', 'business-contacts'],
];

/** The details read, as far as PartyDetails reads it. */
interface DetailsQuery {
    data?: FigureRows;
    error?: unknown;
    isError: boolean;
    refetch: () => unknown;
}

/** The contact's details: bars until they land, then its fields; a failed read says the details did not load. */
function PartyDetails({ definition, query }: { definition: FigureDefinition; query: DetailsQuery }) {
    const intl = useIntl();
    const rows = query.data;
    const row = rows?.rows[0];
    return (
        // Keyed by the error, so a retry shows the bars again.
        <FigureReveal
            key={query.isError ? 'error' : 'rows'}
            loading={!rows && !query.isError}
            skeleton={<RecordFieldsSkeleton fields={DETAIL_FIELDS.length} />}
            skeletonClassName="px-4 pt-1 pb-4"
        >
            {rows && row ? (
                // The record read puts the contact first; its fields follow.
                <RecordFields columns={rows.columns.slice(1)} cells={row.slice(1)} className="px-4 pt-1 pb-4" />
            ) : (
                <FigureStatus
                    definition={definition}
                    empty={false}
                    query={query}
                    failed={intl.formatMessage({ id: 'ai-chat.figure.party.details-failed' })}
                />
            )}
        </FigureReveal>
    );
}

const textOf = (cell: FigureCell | undefined): string | null => (typeof cell === 'string' && cell.trim() ? cell : null);

/** A section: the rows its own source read, as a table Figure's, under their count and order; no Show more. */
function PartySection({ definition, defaultOpen }: { definition: FigureDefinition; defaultOpen: boolean }) {
    const intl = useIntl();
    const query = useReadAiChatFigureRowsQuery(definition, { skip: !definition.source });
    const rows = query.data;
    const count = rows ? figureCount(intl, definition, rows) : '';
    // A cut section's CSV says so, as a table Figure's does.
    const { copyCsv } = useFigureExport(definition.title ?? '', rows, rows?.partial ? count : null);
    const sort = rows ? figureSort(intl, definition, rows) : null;
    return (
        <BriefSection
            icon={figureTile(definition).icon}
            title={definition.title ?? ''}
            // A cut read with no total (searchCases reports none) gets no badge: its row count is not the total.
            count={rows ? (rows.total ?? (rows.partial ? undefined : rows.count)) : undefined}
            defaultOpen={defaultOpen}
        >
            {/* Keyed by the error, so a retry shows the bars again. */}
            <SectionReveal key={query.isError ? 'error' : 'rows'} loading={!rows && !query.isError}>
                {rows && rows.rows.length > 0 ? (
                    <div className="pb-1.5">
                        <p className={cn('text-caption text-foreground-tertiary m-0 pb-2', SECTION_BODY_CLASS_NAME)}>
                            {count}
                            {sort ? ` · ${sort}` : null}
                        </p>
                        <FigureTable rows={rows} copyCsv={copyCsv} />
                    </div>
                ) : (
                    // "No invoices match" names the section's records; a failure names the 360 view.
                    <FigureStatus definition={{ ...definition, kind: 'party360' }} empty={!!rows} query={query} />
                )}
            </SectionReveal>
        </BriefSection>
    );
}

/**
 * A contact's 360 view, laid out as the brief Figure is: the contact's name, relationship and status, and Open contact;
 * four key numbers and the contact's details; then one accordion section per table the answer gave it, the first open.
 * The header reads the contact's row from the Figure's source as a table Figure reads its rows, the details read it
 * again as a record card does, and each section reads its own source.
 */
export function PartyFigure({ definition }: { definition: FigureDefinition }) {
    const intl = useIntl();
    const onInternalLinkNavigate = useAiChatInternalLinkHandler();
    const label = (id: string) => intl.formatMessage({ id: `ai-chat.figure.party.${id}` });
    // The rows endpoint checks the header's read as a table, which needs a title and columns.
    const query = useReadAiChatFigureRowsQuery(
        {
            kind: 'table',
            title: 'Contact',
            source: definition.source,
            columns: HEADER_FIELDS.map((field) => ({ field, label: field })),
        },
        { skip: !definition.source }
    );
    // The details read takes the 360's filter alone: a record refuses the name, sort or limit a 360 view lets by.
    const detailsQuery = useReadAiChatFigureRowsQuery(
        {
            kind: 'record',
            title: 'Contact details',
            source: definition.source && {
                tool: definition.source.tool,
                arguments: { filter: definition.source.arguments?.filter },
            },
            columns: DETAIL_FIELDS.map(([field, labelId]) => ({ field, label: label(labelId) })),
        },
        { skip: !definition.source }
    );
    const rows = query.data;
    const contact = rows?.rows[0]
        ? Object.fromEntries(rows.columns.map(({ field }, index) => [field, rows.rows[0][index]]))
        : null;
    const record = contact ? recordOf(contact.contact ?? null) : null;
    const loading = !rows && !query.isError;
    const tile = figureTile(definition);

    const lastBdEffort = textOf(contact?.lastBdEffort);
    const caseCount = contact?.caseCount;
    const keyNumbers = [
        { label: label('segment'), value: textOf(contact?.segmentTypeName) ?? <MissingValue /> },
        { label: label('service-level'), value: textOf(contact?.serviceLevelTypeName) ?? <MissingValue /> },
        {
            label: label('cases'),
            value: typeof caseCount === 'number' ? intl.formatNumber(caseCount) : <MissingValue />,
        },
        {
            label: label('last-bd-effort'),
            value: lastBdEffort ? (
                <FormattedDate value={dateStringToDateObject(lastBdEffort)} fallback={lastBdEffort} />
            ) : (
                <MissingValue />
            ),
        },
    ];
    const meta = [textOf(contact?.relationshipTypeName), textOf(contact?.statusName)].filter(Boolean).join(' · ');

    return (
        <FigureFrame
            icon={tile.icon}
            tileClassName={tile.className}
            title={record?.label || label('title-pending')}
            meta={meta || null}
            busy={loading}
            className={cn('my-3', HEADER_WRAP_CLASS_NAME)}
            actions={
                record ? (
                    <Button
                        variant="secondary"
                        className={VIEW_BRIEF_CLASS_NAME}
                        nativeButton={false}
                        role="link"
                        render={<RouterLink to={pathForResult(record.entityType, record.id)} />}
                        data-ai-chat-link="internal"
                        onClick={(event) => {
                            if (!isModifiedClick(event)) {
                                onInternalLinkNavigate();
                            }
                        }}
                    >
                        {label('open-contact')}
                    </Button>
                ) : undefined
            }
        >
            <FigureReveal
                key={query.isError ? 'error' : 'rows'}
                loading={loading}
                skeleton={<KeyNumbersSkeleton count={keyNumbers.length} />}
                skeletonClassName={KEY_NUMBERS_CLASS_NAME}
            >
                {contact ? (
                    <>
                        <KeyNumbers numbers={keyNumbers} />
                        <PartyDetails definition={definition} query={detailsQuery} />
                    </>
                ) : (
                    <FigureStatus definition={definition} empty={!!rows} query={query} />
                )}
            </FigureReveal>
            {(definition.sections ?? []).map((section, index) => (
                <PartySection key={`${section.source?.tool}-${index}`} definition={section} defaultOpen={index === 0} />
            ))}
        </FigureFrame>
    );
}
